import { useCreateHeliosDataTablesExportMutation } from 'store/services/helios/dataHelios';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import type { ExportDataFromTableToFile } from 'types/dataHelios';
import { DataTableFormatType } from 'types/dataHelios';
import { FileExtension, FileMimeType } from 'types';
import { isMobileApp } from 'utils/common';
import { downloadBase64File } from 'utils/file/downloadFile';

type ExportParams = ExportDataFromTableToFile & {
  fileName: string;
};

type UseHeliosDataExportReturn = {
  exportData: (params: ExportParams) => Promise<boolean>;
  isExporting: boolean;
};

const useHeliosDataExport = (): UseHeliosDataExportReturn => {
  const { t } = useTranslation();
  const mobileApp = isMobileApp();
  const [createHeliosDataTablesExport, { isLoading: isExporting }] = useCreateHeliosDataTablesExportMutation();

  const exportData = useCallback(
    async ({ fileName, exportDataTableFormatType, ...rest }: ExportParams): Promise<boolean> => {
      try {
        const result = await createHeliosDataTablesExport({
          exportDataTableFormatType,
          ...rest,
        }).unwrap();

        const isPdf = exportDataTableFormatType === DataTableFormatType.Pdf;
        const extension = isPdf ? FileExtension.Pdf : FileExtension.Excel;
        const mimeType = isPdf ? FileMimeType.Pdf : FileMimeType.Excel;

        downloadBase64File({ base64: result, fileName, extension, mimeType });

        if (!mobileApp) {
          toast.success(t('helios.listing.exportModal.success'));
        }

        return true;
      } catch {
        toast.error(t('common.failedToDownload'));

        return false;
      }
    },
    [createHeliosDataTablesExport, mobileApp, t]
  );

  return { exportData, isExporting };
};

export { useHeliosDataExport };
